<template>
  <div class="discuss-forum position-relative">
    <!-- Breadcrumbs Messages -->
    <Breadcrumb v-if="projectAvailable" :home="breadcrumbHome" :model="breadcrumbItems" class="forum-breadcrumbs">
      <template #item="{ item }">
        <button class="btn btn-link p-menuitem-link" @click="item.command">
          <template v-if="item.icon">
            <span class="p-menuitem-icon" :class="item.icon"></span>
          </template>
          <template v-if="item.label">
            <span class="p-menuitem-text">
              {{ item.label }}
            </span>
          </template>
          <template v-else>
            <Skeleton width="60px" height="1rem" />
          </template>
        </button>
      </template>
    </Breadcrumb>
    <Watermark class="forum-watermark" />
    <!-- Feedback Messages -->
    <Message v-for="msg of messages" :severity="msg.severity" :key="msg.content" :closable="true">
      {{ msg.content }}
    </Message>
    <!-- Loading Indicator -->
    <template v-if="!projectAvailable">
      <loading loadingMessage="Loading..." />
    </template>

    <template v-else>
      <!-- dynamically load components -->
      <keep-alive :include="['ForumTopics', 'ForumSingleTopic', 'ForumSingleThought']">
        <component
          :is="currentComponent.name"
          v-bind="currentComponent.props"
          @show-topics="showTopics"
          @show-topic="showTopic"
          @show-thought="showThought"
          @create-thought="showCreateThought"
          @create-comment="showCreateComment"
          @edit-thought="showEditThought"
          @push-message="pushMessage"
          @clear-messages="clearMessages"
          @loaded-topic-title="setTopicTitle"
          @loaded-thought-title="setThoughtTitle"
        ></component>
      </keep-alive>
    </template>
  </div>
</template>

<script>
import { onBeforeMount, ref, watch, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

// stores
import { userStore } from '@/store/user-store';
import { domainStore } from '@/store/domain-store';
import { projectStore } from '@/store/project-store';

// components
import Breadcrumb from 'primevue/breadcrumb';
import Message from 'primevue/message';
import Skeleton from 'primevue/skeleton';
import Loading from '@/components/general/Loading.vue';
import ForumTopics from '@/components/forum/views/Topics.vue';
import ForumSingleTopic from '@/components/forum/views/SingleTopic.vue';
import ForumSingleThought from '@/components/forum/views/SingleThought.vue';
import ForumCreateThought from '@/components/forum/views/CreateThought.vue';
import ForumCreateComment from '@/components/forum/views/CreateComment.vue';
import ForumEditThought from '@/components/forum/views/EditThought.vue';
import Watermark from '@/components/general/Watermark.vue';
// composables
import useAuthentication from '@/composables/user/useAuthentication';
import useHashParams from '@/composables/general/useHashParams';
export default {
  name: 'ForumProject',

  props: {
    domainid: {
      required: true,
      type: Number,
    },
    projectid: {
      required: true,
      type: Number,
    },
    lang: {
      type: String,
      required: false,
      default: 'de',
    },
  },

  setup(props) {
    // set props to store for initialization
    onBeforeMount(() => {
      domainStore.initDomain(props.domainid, props.lang);
      projectStore.setProjectId(props.projectid, false);
    });
    const { initStorageAuthToken, getVirtualUser } = useAuthentication();
    const route = useRoute();
    const { setHashParams } = useHashParams(route);
    const selectedTopic = ref();
    const selectedThought = ref();
    const messages = ref([]);
    let initializedRouteParams = false;

    const noComponent = {
      name: null,
      props: {},
    };
    const selectedComponent = ref({
      name: 'ForumTopics',
      props: { projectid: props.projectid },
    });

    //
    //-----------------COMPUTED------------------
    //

    const project = computed(() => {
      return projectStore.getState().project;
    });
    const projectAvailable = computed(() => {
      if (project.value) {
        return project.value.id ? true : false;
      }
      return false;
    });
    const currentComponent = computed(() => {
      if (!projectAvailable.value) {
        return noComponent;
      } else {
        return selectedComponent.value;
      }
    });

    //
    //-----------------METHODS------------------
    // methods - messages for user
    //

    function clearMessages(params) {
      messages.value = [];
    }
    function pushMessage(params) {
      messages.value.push(params);
    }
    function resetHashParams() {
      setHashParams({
        topic_id: '',
        thought_id: '',
      });
    }

    //
    //-----------------METHODS------------------
    // methods - navigation
    //

    function showTopics(params) {
      // we can clear messages in topic-list-view
      clearMessages();
      selectedThought.value = null;
      selectedTopic.value = null;
      selectedComponent.value = {
        name: 'ForumTopics',
        props: { projectid: props.projectid },
      };
      resetHashParams();
    }

    function showTopic(params) {
      // we can clear messages in topic-view
      clearMessages();
      if (params.topicid) {
        selectedThought.value = null;
        selectedTopic.value = params;
        selectedComponent.value = {
          name: 'ForumSingleTopic',
          props: { topicid: params.topicid },
        };
        setHashParams({
          topic_id: params.topicid,
          thought_id: '',
        });
      } else {
        showTopics();
      }
    }

    function showThought(params) {
      if (params.thoughtid) {
        selectedThought.value = params;
        selectedComponent.value = {
          name: 'ForumSingleThought',
          props: { thoughtid: params.thoughtid },
        };
        setHashParams({
          topic_id: selectedTopic.value.topicid,
          thought_id: params.thoughtid,
        });
      } else {
        showTopic(selectedTopic.value);
      }
    }

    function showCreateThought(params) {
      if (params.topicid) {
        selectedThought.value = null;
        selectedTopic.value = params;
        selectedComponent.value = {
          name: 'ForumCreateThought',
          props: { topicid: params.topicid },
        };
        setHashParams({
          topic_id: params.topicid,
          thought_id: '',
        });
      } else {
        showTopic(selectedTopic.value);
      }
    }

    function showEditThought(params) {
      if (params.topic.id && params.thought.id) {
        selectedThought.value = {
          thoughtid: params.thought.id,
          name: params.thought.title,
        };
        selectedTopic.value = {
          topicid: params.topic.id,
          name: params.topic.title,
        };
        selectedComponent.value = {
          name: 'ForumEditThought',
          props: { topic: params.topic, thought: params.thought },
        };
        setHashParams({
          topic_id: params.topic.id,
          thought_id: params.thought.id,
        });
      } else {
        showThought(selectedThought.value);
      }
    }

    function showCreateComment(params) {
      if (params.topic.id && params.thought.id) {
        selectedThought.value = {
          thoughtid: params.thought.id,
          name: params.thought.title,
        };
        selectedTopic.value = {
          topicid: params.topic.id,
          name: params.topic.title,
        };
        selectedComponent.value = {
          name: 'ForumCreateComment',
          props: { topic: params.topic, thought: params.thought },
        };
        setHashParams({
          topic_id: params.topic.id,
          thought_id: params.thought.id,
        });
      } else {
        showThought(selectedThought.value);
      }
    }

    //
    //-----------------METHODS------------------
    // helpers
    //
    function shortenBreadcrumbName(string) {
      return string;
    }

    function setTopicTitle(id, title) {
      if (selectedTopic.value.topicid == id) {
        selectedTopic.value.name = title;
      }
    }

    function setThoughtTitle(id, title) {
      if (selectedThought.value.thoughtid == id) {
        selectedThought.value.name = title;
      }
    }

    //
    //-----------------REFS------------------
    // Breadcrumbs
    //
    const breadcrumbHome = computed(() => {
      return {
        icon: 'pi pi-home',
        label: projectAvailable.value ? ' ' + shortenBreadcrumbName(project.value.name) : '',
        command: e => {
          showTopics();
          e.stopPropagation();
        },
      };
    });
    const breadcrumbItems = computed(() => {
      const items = [];
      if (selectedTopic.value) {
        items.push({
          // label: 'Themen',
          label: selectedTopic.value.name ? shortenBreadcrumbName(selectedTopic.value.name) : '',
          disabled: false,
          command: e => {
            showTopic(selectedTopic.value);
            e.stopPropagation();
          },
        });
        if (currentComponent.value.name == 'ForumCreateThought') {
          items.push({
            label: 'Neues Thema erstellen',
            disabled: true,
          });
        } else if (selectedThought.value) {
          items.push({
            // label: 'Thema',
            label: selectedThought.value.name ? shortenBreadcrumbName(selectedThought.value.name) : '',
            disabled: false,
            command: e => {
              showThought(selectedThought.value);
              e.stopPropagation();
            },
          });
          if (currentComponent.value.name == 'ForumCreateComment') {
            items.push({
              label: 'Neuen Beitrag erstellen',
              disabled: true,
            });
          } else if (currentComponent.value.name == 'ForumEditThought') {
            items.push({
              label: 'Thema bearbeiten',
              disabled: true,
            });
          }
        }
      }
      // disable last item:
      if (items.length > 0) {
        items[items.length - 1].disabled = true;
      }
      return items;
    });

    //
    //-----------------WATCH------------------
    // check hashparams for correct initialization

    watch(route, () => {
      if (!initializedRouteParams) {
        const topic_id = parseInt(route.query.topic_id);
        const thought_id = parseInt(route.query.thought_id);
        if (topic_id) {
          if (thought_id) {
            selectedTopic.value = {
              topicid: topic_id,
              name: '',
            };
            showThought({ thoughtid: thought_id });
          } else {
            showTopic({ topicid: topic_id });
          }
        } else {
          resetHashParams();
        }
        // make sure to only check once on startup
        initializedRouteParams = true;
      }
    });

    // watch on project-store to init virtual-user if required
    watch(
      () => projectStore.getState().project,
      (project, oldProject) => {
        if (!userStore.getState().authToken && process.env.VUE_APP_ENABLE_VIRTUAL_USER) {
          // try to init/get virtual user for eventual anonymous votes/comments/thoughts
          getVirtualUser()
            .then(success => {
              if (!success) {
                console.log('no successinitialized virtual user');
              }
            })
            .catch(e => {
              console.log('error getting virtual user');
            });
        }
      }
    );

    return {
      // REFS
      breadcrumbHome,
      breadcrumbItems,
      selectedTopic,
      selectedThought,
      messages,
      // COMPUTED
      currentComponent,
      projectAvailable,

      // METHODS
      showTopics,
      showTopic,
      showThought,
      showCreateThought,
      showCreateComment,
      showEditThought,
      pushMessage,
      clearMessages,
      setTopicTitle,
      setThoughtTitle,
    };
  },

  components: {
    ForumTopics,
    ForumSingleTopic,
    ForumSingleThought,
    ForumCreateThought,
    ForumCreateComment,
    ForumEditThought,
    Loading,
    Breadcrumb,
    Message,
    Skeleton,
    Watermark,
  },
};
</script>

<style scoped>
.forum-watermark {
  width: 200px;
  position: absolute;
  right: 0px;
  top: 0px;
}

@media screen and (max-width: 940px) {
  .forum-watermark {
    width: 150px;
    top: -15px;
  }
}

@media screen and (max-width: 768px) {
  .forum-watermark {
    width: 150px;
    top: -15px;
  }
}
</style>
